import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');
  const [statusFilter, setStatusFilter] = useState<'全部' | '连载中' | '已完结'>('全部');

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];

  const filteredComics = useMemo(() => {
    let result = comics.filter((c) => c.categories.includes(slug || 'shaonian'));

    if (statusFilter !== '全部') {
      result = result.filter((c) => c.status === statusFilter);
    }

    if (sortBy === 'latest') {
      result.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
    } else if (sortBy === 'popular') {
      result.sort((a, b) => b.views - a.views);
    } else {
      result.sort((a, b) => b.rating - a.rating);
    }

    return result;
  }, [slug, sortBy, statusFilter]);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '漫画分类', to: '/category/shaonian' },
              { label: currentCategory.name },
            ]}
          />

          <div className="mt-6">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              {currentCategory.name}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              共找到 <strong className="text-primary-500">{filteredComics.length}</strong> 部{currentCategory.name}漫画
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 mt-6">
            <div className="flex items-center gap-1 px-1 py-1 bg-background-100 rounded-full">
              {(['全部', '连载中', '已完结'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-4 py-1.5 rounded-full text-xs md:text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    statusFilter === s
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-950'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-1 ml-auto">
              <span className="text-xs text-foreground-400 mr-1">排序：</span>
              {([
                { key: 'latest' as const, label: '最新' },
                { key: 'popular' as const, label: '最热' },
                { key: 'rating' as const, label: '评分' },
              ]).map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                    sortBy === opt.key
                      ? 'bg-secondary-100 text-secondary-700'
                      : 'text-foreground-500 hover:text-foreground-950'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

          {filteredComics.length > 0 ? (
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {filteredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="mt-16 text-center py-16">
              <div className="w-16 h-16 mx-auto flex items-center justify-center bg-background-100 rounded-full mb-4">
                <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">暂无符合条件的漫画</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}